import * as React from 'react';
import {FunctionComponent} from 'react';
import {observer} from 'mobx-react-lite';
import {PresenterProps, withPresenter} from '../../../support/with_presenter';
import {MediaItemListPresenter} from './MediaItemListPresenter';
import {ExpandedPart} from '../../../business/expanded_parts/ExpandedPartsProvider';
import {AddMediaWizard} from './AddMediaWizard/AddMediaWizard';
import {Media} from '../../../models/Media';
import {toHumanReadableLanguage} from '../../../models/Language';
import {deviceRequirementsToString} from '../../../models/DeviceRequirementRule';
import {Anchor, AnchorOffsetType, AnchorPoint} from '../../../models/Anchor';

function generateAnchorText(anchor: Anchor): string {
    switch (anchor.offset_type) {
        case AnchorOffsetType.TimeSeconds:
            switch (anchor.anchor_point) {
                case AnchorPoint.Start:
                    return `at ${anchor.offset_value} seconds from the start of this part`;
                case AnchorPoint.End:
                    return `at ${anchor.offset_value} seconds before the end of this part`;
            }
    }
}

interface OwnProps {
    expandedPart: ExpandedPart;
    media: Media[];
}

const MediaItemListComponent: FunctionComponent<OwnProps & PresenterProps<MediaItemListPresenter>> = observer(
    ({expandedPart, media, presenter}) => {
        return (
            <div className="media-item-list">
                <div className="media-item-list__content">
                    {presenter.mediaItems.map((mediaItem) => {
                        const item = media.find((m) => m.id === mediaItem.mediaId);
                        if (item) {
                            const language = item.language;
                            return (
                                <div key={mediaItem.__uuid} className="media-item">
                                    <div className="media-item__thumbnail">
                                        {item.thumbnail_url && <img src={item.thumbnail_url} />}
                                    </div>
                                    <div className="media-item__title">
                                        <strong>
                                            {item.title} @ {mediaItem.playbackrate}x
                                        </strong>
                                        <br />
                                        {generateAnchorText(mediaItem.anchor)}
                                        <br />
                                        Equipment needed: {deviceRequirementsToString(item.device_requirements)}
                                        <br />
                                        {language === null ? null : ' in ' + toHumanReadableLanguage(language)}
                                    </div>
                                    <button
                                        className="media-item__trash ion-md-trash"
                                        onClick={() => presenter.onTrashMediaItem(mediaItem)}
                                    />
                                </div>
                            );
                        }
                        return (
                            <div key={mediaItem.__uuid} className="media-item">
                                <div className="media-item__title">
                                    {mediaItem.mediaId} @ {mediaItem.playbackrate}x{' '}
                                    {generateAnchorText(mediaItem.anchor)}
                                </div>
                                <button
                                    className="media-item__trash ion-md-trash"
                                    onClick={() => presenter.onTrashMediaItem(mediaItem)}
                                />
                            </div>
                        );
                    })}
                </div>
                <div className="media-item-list__footer">
                    <button
                        className="btn btn-secondary ion-ios-add icon-left pull-right"
                        onClick={() => presenter.onAddMediaItemClick()}
                    >
                        Add media
                    </button>
                    {presenter.addMediaItemWizardVisible && (
                        <AddMediaWizard
                            media={media}
                            expandedPart={expandedPart}
                            onAdd={(mediaItem) => presenter.onAddMediaItem(mediaItem)}
                            onCancel={() => presenter.onCancelAddMediaItemWizard()}
                        />
                    )}
                </div>
            </div>
        );
    },
);
export const MediaItemList = withPresenter<MediaItemListPresenter, OwnProps>(
    (props, component) => new MediaItemListPresenter(props.expandedPart.__uuid, component.mediaItemsInteractor),
    MediaItemListComponent,
);
